import React, { FC, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useFormContext, useWatch, Controller } from 'react-hook-form';

import {
  ReservationFlowMe,
  ReservationFlowSelectedPaymentMethodNewCreditCard,
} from 'src/interfaces/reservation_flow';
import { isStripeErrorInputField } from 'src/state-machines/purchase/functions/is-stripe-error-input-field';
import { ProductPurchaseServiceContext } from 'src/contexts/state-machines/purchase/ProductPurchaseServiceContext';
import { RSNote } from 'src/reservation-page/RSNote';

import { SectionFieldset } from 'src/organisms/product-purchase/SectionFieldset';
import { PaymentMethodInputNewCreditCard } from 'src/organisms/product-purchase/PaymentMethodInputNewCreditCard';
import { PaymentMethodInput } from 'src/organisms/product-purchase/PaymentMethodInput';

import { FormValues } from '../..';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { TokenResponse, TokenObj } from '@mul-pay/mptoken-js';
import { useIsMulpayOrderEnabled } from 'src/hooks/api/reservation-flow/useIsMulpayOrderEnabled';

export interface PaymentMethodFieldsetProps {
  user: ReservationFlowMe;
}

export const PaymentMethodFieldset: FC<PaymentMethodFieldsetProps> = ({
  user,
}) => {
  const intl = useIntl();
  const stripe = useStripe();
  const elements = useElements();
  const [_state, send] = ProductPurchaseServiceContext.useActor();

  const dataStripeError = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.stripeError,
  );

  const dataMerchantPublicId = ProductPurchaseServiceContext.useSelector(
    (state) => state.context.data.merchantPublicId,
  );

  const { control, setValue } = useFormContext<FormValues>();

  const watchedPaymentMethod = useWatch({
    control,
    name: 'paymentMethod',
  });

  const isMulpayOrderEnabled = useIsMulpayOrderEnabled(
    dataMerchantPublicId || '',
  );

  useEffect(() => {
    if (typeof watchedPaymentMethod === 'undefined') return;

    send({
      type: 'INPUT_PAYMENT_METHOD',
      payload: { paymentMethod: watchedPaymentMethod },
    });
  }, [send, watchedPaymentMethod]);

  useEffect(() => {
    stripe &&
      send({
        type: 'SET_STRIPE_INSTANCE',
        payload: { stripeV3Instance: stripe },
      });
  }, [send, stripe]);

  useEffect(() => {
    if (typeof isMulpayOrderEnabled === 'undefined') return;

    send({
      type: 'SET_IS_MULPAY_ORDER_ENABLED',
      payload: { isMulpayOrderEnabled },
    });
  }, [send, isMulpayOrderEnabled]);

  const handleRegisteredCardSelected = () => {
    if (!user?.credit_card?.onfile_card_identifier) {
      return;
    }

    send({
      type: 'SET_MULPAY_CARD_DATA',
      payload: {
        mulpayCardData: {
          type: 'existing',
          mulpayOnfileCardIdentifier: user.credit_card.onfile_card_identifier,
        },
      },
    });
  };

  const handleMulpayTokenChange = useCallback(
    (tokenResponse?: TokenResponse) => {
      if (tokenResponse?.result != 'success') {
        send({
          type: 'RESET_MULPAY_INPUT',
        });
        return;
      }

      // TokenResponse　は　TokenObj　と　ErrorResponse　の　union　だが、discriminated union　になっておらず
      // resultの値からは型が推論されないので、　as　で強制型変換を行っている
      const tokenObj = tokenResponse as TokenObj;
      setValue('paymentMethod', {
        type: 'new_credit_card',
        creditCardInfo: {},
        isStoreCreditCard: true, // 月謝は新規カードの場合、常に保存する
      });
      send({
        type: 'SET_MULPAY_MP_TOKEN',
        payload: { mulpayMpToken: tokenObj.tokenList[0] },
      });
    },
    [send, setValue],
  );

  const handlePaymentElementChange = async (
    event: StripePaymentElementChangeEvent,
  ) => {
    if (!stripe || !elements || !event.complete) {
      return;
    }

    const { error: submitError } = await elements.submit();

    if (submitError) {
      send({ type: 'RESET_STRIPE_PAYMENT_ELEMENTS' });
      return;
    }
    setValue('paymentMethod', {
      type: 'new_credit_card',
      // TODO: [3ds] legacy排除後はこの形で保持しなくても良い
      creditCardInfo: {},
      isStoreCreditCard: (
        watchedPaymentMethod as ReservationFlowSelectedPaymentMethodNewCreditCard
      )?.isStoreCreditCard,
    });

    send({
      type: 'SET_STRIPE_PAYMENT_ELEMENTS',
      payload: { stripeElements: elements },
    });
  };

  return (
    <SectionFieldset
      legend={intl.formatMessage({
        defaultMessage: 'お支払い方法',
        id: 'pHuvtf',
        description:
          'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
      })}
    >
      {typeof dataStripeError !== 'undefined' &&
        !isStripeErrorInputField(dataStripeError) && (
          <div className="mb-2.5">
            <div>
              {(() => {
                switch (dataStripeError.code) {
                  case 'card_declined':
                    return (
                      <RSNote
                        body={intl.formatMessage({
                          defaultMessage:
                            '利用不可のカード番号です。今一度ご確認ください。',
                          id: 'udiAxi',
                          description:
                            'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
                        })}
                        variant="capital"
                      />
                    );
                  case 'expired_card':
                    return (
                      <RSNote
                        body={intl.formatMessage({
                          defaultMessage:
                            '有効期限切れのカード番号です。今一度ご確認ください。',
                          id: 'WR0/PS',
                          description:
                            'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
                        })}
                        variant="capital"
                      />
                    );
                  default:
                    return (
                      <RSNote
                        body={intl.formatMessage({
                          defaultMessage:
                            'クレジットカード決済ができませんでした。今一度クレジットカード情報を確認してください。',
                          id: 'IZVFi0',
                          description:
                            'src/templates/merchants/resources/book/components/product-purchase-modal/SubscriptionPurchaseModal/components/MainView/components/PaymentMethodFieldset/index.tsx',
                        })}
                        variant="capital"
                      />
                    );
                }
              })()}
            </div>
          </div>
        )}

      {(() => {
        if (!user.credit_card) {
          return (
            <Controller
              control={control}
              name="paymentMethod"
              render={({ field: { value, onChange, onBlur, ref } }) => {
                return (
                  <PaymentMethodInputNewCreditCard
                    ref={ref}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isMonthlyBilling={true}
                    handlePaymentElementChange={handlePaymentElementChange}
                    // isMulapayOrderEnabled はモーダルからの決済の場合は state に格納するが、
                    // この Component は Product の決済情報入力の汎用的な Component で、この中では state を参照しないため props で渡す
                    isMulpayOrderEnabled={isMulpayOrderEnabled}
                    handleMulpayTokenChange={handleMulpayTokenChange}
                    merchantPublicId={dataMerchantPublicId || ''}
                  />
                );
              }}
            />
          );
        }

        return (
          <Controller
            control={control}
            name="paymentMethod"
            render={({ field: { value, onChange, onBlur, ref } }) => {
              return (
                <PaymentMethodInput
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  userCreditCard={user.credit_card}
                  isMonthlyBilling={true}
                  handlePaymentElementChange={handlePaymentElementChange}
                  // isMulapayOrderEnabled はモーダルからの決済の場合は state に格納するが、
                  // この Component は Product の決済情報入力の汎用的な Component で、この中では state を参照しないため props で渡す
                  isMulpayOrderEnabled={isMulpayOrderEnabled}
                  handleMulpayTokenChange={handleMulpayTokenChange}
                  handleRegisteredCardSelected={handleRegisteredCardSelected}
                  merchantPublicId={dataMerchantPublicId || ''}
                />
              );
            }}
          />
        );
      })()}
    </SectionFieldset>
  );
};
